/* A project's location arrives from the API as a single "lat,lng" string, and goes back the same
   way. Every read and write of that format goes through here, so a malformed pair is judged the
   same way everywhere rather than each caller inventing its own rule. */

/* Number("") and Number(" ") are both 0, so a blank half has to be rejected before the cast —
   otherwise a pair such as "54.5," places the project on the equator instead of being skipped. */
export const parseCoordinate = (value: string | undefined | null): number | undefined => {
  if (!value?.trim()) return undefined;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

/* The halves as typed, for form fields and read-only display. Whitespace-only is null, so a pair
   such as "54.5,   " does not put spaces in an input and round-trip back to the API. */
export const splitCoordinates = (
  locationCoordinates: string | null | undefined
): { latitude: string | null; longitude: string | null } => {
  const [latitudeValue, longitudeValue] = locationCoordinates?.split(",") ?? [];

  return {
    latitude: latitudeValue?.trim() || null,
    longitude: longitudeValue?.trim() || null,
  };
};

/* Both halves have to parse, or there is nowhere to put the project on a map. */
export const toCoordinateFromParts = (
  latitude: string | null | undefined,
  longitude: string | null | undefined
): { lat: number; lng: number } | undefined => {
  const lat = parseCoordinate(latitude);
  const lng = parseCoordinate(longitude);

  return lat !== undefined && lng !== undefined ? { lat, lng } : undefined;
};

export const toCoordinate = (
  locationCoordinates: string | null | undefined
): { lat: number; lng: number } | undefined => {
  const { latitude, longitude } = splitCoordinates(locationCoordinates);

  return toCoordinateFromParts(latitude, longitude);
};

/* A half-filled pair is still stored: discarding a lone latitude would silently lose what the user
   typed, and the readers above already treat it as unplaceable. */
export const formatCoordinates = (
  latitude: string | null | undefined,
  longitude: string | null | undefined
): string | null => (latitude || longitude ? `${latitude ?? ""},${longitude ?? ""}` : null);
